import { useQuery } from '@tanstack/react-query'
import type { Capability } from '@gridfit/core/principals'
import { fetchMe } from '@/api/client'
import type { MeResponse } from '@/api/client'

/**
 * Re-exported, not declared here — `MeResponse` moved to `api/client.ts`
 * (web-pre-deploy item 11) so the module that actually builds one from the
 * wire owns its shape. Kept exported from this file because
 * `shell/landing.ts` and `shell/nav.ts` import it from here and neither is
 * touched by that move; a re-export keeps them working without a second
 * kind of import path for the same type.
 */
export type { MeResponse }

/**
 * The shell's own identity read. Not a `queryHook`: `/api/me` is a ROUTE, not
 * a registry query, because `defineQuery` refuses an empty capability list
 * and a share principal holds none (design §4.1). It therefore has no shape,
 * no fold and no fragments — it changes only at sign-in and sign-out.
 *
 * The cache key is `['session', 'me']` — a CLIENT-ONLY key with no registered
 * query shape behind it. `commandHook`'s own `observing` filter (`api/
 * hooks.ts`) drops any active cache key whose first segment isn't a name
 * `listShapes().queries` recognises, precisely so this key is never sent as
 * an observed binding on some unrelated command dispatch and 400 the whole
 * thing. That filter's own doc comment names this exact key as the reason it
 * exists — do not rename `['session', 'me']` without re-reading it.
 *
 * `staleTime: Infinity`: this value changes ONLY at sign-in/sign-out, never
 * on a timer or a background refetch. That makes an explicit invalidation
 * REQUIRED after a successful sign-in or set-password, not optional — see
 * `LoginRoute`/`SetPasswordRoute`'s own doc comments for where that happens
 * and how it was verified. Without it, a shell that read `useSession()`
 * before the user signed in (e.g. to decide whether to show this very
 * screen) would keep serving that pre-sign-in cached value forever, because
 * an `Infinity` staleTime never goes stale on its own and a plain remount
 * only refetches stale data.
 *
 * `retry: false`: a real 401 is a normal, expected outcome (an anonymous
 * visitor), not a transient fault to retry through.
 *
 * COVERAGE, STATED PLAINLY (review round 1, Important 1): nothing under
 * `packages/web` mounts this hook yet. A grep for `useSession` across
 * `packages/web/src` and `packages/web/test` turns up only this definition
 * and two prose mentions in `LoginRoute.tsx`'s doc comment — no component
 * calls it. That means `queryFn`, BOTH its branches (the 401→null path and
 * the ok-response path), `staleTime`, and `has()` all have ZERO test
 * coverage today. `packages/api/test/me.workers.test.ts` proves the SERVER
 * answers 401 for an anonymous caller — a fact about a different process,
 * in a different package, that cannot execute a line of this file (proven,
 * not merely reasoned: replacing `return null` with a `throw` here leaves
 * that worker suite exactly as green as before, since it never imports or
 * runs this module). Task 11 is expected to be the first real consumer
 * (a shell reading `useSession()` to decide what to render/guard), and
 * therefore the first thing to actually cover any of the above — until then,
 * this is unexercised, not merely under-exercised.
 */
export function useSession(): { me: MeResponse | undefined; isLoading: boolean; isSignedIn: boolean } {
  const query = useQuery({
    queryKey: ['session', 'me'],
    // `fetchMe` (`api/client.ts`), not an inline `fetch`: the 401→`null`
    // contract and the throw-on-anything-else contract are unchanged, they
    // just live in the one module that owns transport now. Its single-flight
    // is what stops this query and `outbox/clearance.ts`'s independent boot
    // check issuing two `GET /api/me` requests.
    queryFn: fetchMe,
    staleTime: Infinity,
    retry: false,
  })

  return {
    me: query.data ?? undefined,
    isLoading: query.isLoading,
    isSignedIn: query.data != null,
  }
}

export function has(me: MeResponse | undefined, capability: Capability): boolean {
  return me?.capabilities.includes(capability) ?? false
}
